import { useEffect, useMemo, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    FormControlLabel,
    LinearProgress,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';

import {
    createAdminNotification,
    createEmptyNotificationDraft,
    createNotificationDraft,
    deleteAdminNotification,
    getAdminNotificationList,
    updateAdminNotification,
    type UserCommunicationNotification,
    type UserCommunicationNotificationDraft,
    type UserNotificationSeverity,
} from '../admin/adminUserCommunicationNotificationsApi';
import { formatApiDateTime } from '../admin/dateTimeUtils';

const severityOptions: Array<{ value: UserNotificationSeverity; label: string; color: 'info' | 'warning' | 'error' | 'success' }> = [
    { value: 'info', label: 'Info', color: 'info' },
    { value: 'warning', label: 'Warning', color: 'warning' },
    { value: 'error', label: 'Error', color: 'error' },
    { value: 'success', label: 'Success', color: 'success' },
];

function formatDateTime(value: string | null): string {
    return value ? formatApiDateTime(value) : 'Not set';
}

function validateDraft(draft: UserCommunicationNotificationDraft): string | null {
    if (!draft.message.trim()) {
        return 'Message is required';
    }

    if (draft.startsAt && draft.endsAt && new Date(draft.endsAt) <= new Date(draft.startsAt)) {
        return 'End time must be after start time';
    }

    if (Boolean(draft.ctaLabel.trim()) !== Boolean(draft.ctaUrl.trim())) {
        return 'CTA label and CTA URL must be provided together';
    }

    return null;
}

export function UserNotificationsPage() {
    const notify = useNotify();
    const [items, setItems] = useState<UserCommunicationNotification[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [draft, setDraft] = useState<UserCommunicationNotificationDraft>(() => createEmptyNotificationDraft());
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [includeInactive, setIncludeInactive] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const selectedNotification = useMemo(
        () => items.find((item) => item.notificationId === selectedId) ?? null,
        [items, selectedId],
    );

    async function loadItems(nextSearch = search, preferredId: string | null = selectedId) {
        setIsLoading(true);

        try {
            const result = await getAdminNotificationList({
                page: 1,
                pageSize: 100,
                search: nextSearch,
                includeInactive,
            });

            setItems(result.items);
            setTotalCount(result.totalCount);

            const nextSelectedId =
                preferredId && result.items.some((item) => item.notificationId === preferredId)
                    ? preferredId
                    : (result.items[0]?.notificationId ?? null);

            setSelectedId(nextSelectedId);
            setDraft(
                nextSelectedId
                    ? createNotificationDraft(
                          result.items.find((item) => item.notificationId === nextSelectedId) ?? result.items[0],
                      )
                    : createEmptyNotificationDraft(),
            );
        } catch (error) {
            setItems([]);
            setTotalCount(0);
            setSelectedId(null);
            setDraft(createEmptyNotificationDraft());
            notify(error instanceof Error ? error.message : 'Unable to load notifications', { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        void loadItems(search, selectedId);
    }, [search, includeInactive]);

    const isCreating = selectedNotification === null;
    const selectedSeverity = severityOptions.find((option) => option.value === draft.severity) ?? severityOptions[0];

    const handleDraftChange = <TKey extends keyof UserCommunicationNotificationDraft>(
        field: TKey,
        value: UserCommunicationNotificationDraft[TKey],
    ) => {
        setDraft((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const handleCreateNew = () => {
        setSelectedId(null);
        setDraft(createEmptyNotificationDraft());
    };

    const handleSelect = (item: UserCommunicationNotification) => {
        setSelectedId(item.notificationId);
        setDraft(createNotificationDraft(item));
    };

    const handleSave = async () => {
        const validationError = validateDraft(draft);
        if (validationError) {
            notify(validationError, { type: 'warning' });
            return;
        }

        setIsSaving(true);
        try {
            const saved = selectedId
                ? await updateAdminNotification(selectedId, draft)
                : await createAdminNotification(draft);

            notify(selectedId ? 'Notification updated' : 'Notification created', { type: 'info' });
            await loadItems(search, saved.notificationId);
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Unable to save notification', { type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedNotification) {
            return;
        }

        if (!window.confirm(`Delete notification "${selectedNotification.message.slice(0, 60)}"?`)) {
            return;
        }

        setIsDeleting(true);
        try {
            await deleteAdminNotification(selectedNotification.notificationId);
            notify('Notification deleted', { type: 'info' });
            await loadItems(search, null);
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Unable to delete notification', { type: 'error' });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleTogglePublish = () => {
        handleDraftChange('isActive', !draft.isActive);
    };

    return (
        <Card>
            <Title title="User Notifications" />
            {isLoading || isSaving || isDeleting ? <LinearProgress /> : null}
            <CardContent>
                <Stack spacing={3}>
                    <Stack spacing={1}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <NotificationsActiveOutlinedIcon color="primary" />
                            <Typography variant="h4">User Notifications</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            Publish plain text top-banner messages for users. Closing a banner in the app keeps a
                            floating severity icon so users can reopen the message.
                        </Typography>
                    </Stack>

                    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
                        <TextField
                            label="Search notifications"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder="Search message, title, severity, or coupon"
                            fullWidth
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button variant="outlined" onClick={() => setSearch(searchInput.trim())}>
                                Search
                            </Button>
                            <Button
                                variant="text"
                                onClick={() => {
                                    setSearchInput('');
                                    setSearch('');
                                }}
                            >
                                Clear
                            </Button>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={includeInactive}
                                        onChange={(event) => setIncludeInactive(event.target.checked)}
                                    />
                                }
                                label="Inactive"
                            />
                            <Button
                                variant="outlined"
                                startIcon={<RefreshRoundedIcon />}
                                onClick={() => void loadItems(search, selectedId)}
                            >
                                Refresh
                            </Button>
                            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={handleCreateNew}>
                                New
                            </Button>
                        </Stack>
                    </Stack>

                    <Box
                        sx={{
                            display: 'grid',
                            gap: 3,
                            gridTemplateColumns: {
                                xs: '1fr',
                                lg: '360px minmax(0, 1fr)',
                            },
                        }}
                    >
                        <Paper variant="outlined">
                            <Stack spacing={1} sx={{ p: 2.5 }}>
                                <Typography variant="h6">Notifications</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {totalCount} total notification{totalCount === 1 ? '' : 's'}
                                    {search ? ` matching "${search}"` : ''}
                                </Typography>
                            </Stack>
                            <Divider />
                            {items.length === 0 ? (
                                <Box sx={{ p: 2 }}>
                                    <Alert severity="info">
                                        {search
                                            ? 'No notifications match the current search.'
                                            : 'No notifications exist yet. Create the first message to begin.'}
                                    </Alert>
                                </Box>
                            ) : (
                                <List disablePadding sx={{ maxHeight: 620, overflowY: 'auto' }}>
                                    {items.map((item) => {
                                        const severity = severityOptions.find((option) => option.value === item.severity) ?? severityOptions[0];
                                        return (
                                            <ListItemButton
                                                key={item.notificationId}
                                                selected={item.notificationId === selectedId}
                                                onClick={() => handleSelect(item)}
                                                divider
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Chip
                                                                size="small"
                                                                color={severity.color}
                                                                label={severity.label}
                                                                sx={{ minWidth: 76 }}
                                                            />
                                                            <Chip
                                                                size="small"
                                                                color={item.isActive ? 'success' : 'default'}
                                                                label={item.isActive ? 'Published' : 'Draft'}
                                                            />
                                                        </Stack>
                                                    }
                                                    secondary={
                                                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                                                            <Typography variant="body2" color="text.primary">
                                                                {item.title || item.message}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Priority {item.priority} • Updated {formatDateTime(item.updatedAt)}
                                                            </Typography>
                                                        </Stack>
                                                    }
                                                />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            )}
                        </Paper>

                        <Paper variant="outlined">
                            <Stack spacing={2} sx={{ p: 3 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                                    <Stack spacing={0.5}>
                                        <Typography variant="h6">
                                            {isCreating ? 'Create Notification' : 'Edit Notification'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Plain text only. Users can minimize and reopen active messages.
                                        </Typography>
                                    </Stack>
                                    <Chip
                                        color={draft.isActive ? 'success' : 'default'}
                                        label={draft.isActive ? 'Published' : 'Unpublished'}
                                    />
                                </Stack>

                                <FormControlLabel
                                    control={<Switch checked={draft.isActive} onChange={handleTogglePublish} />}
                                    label={draft.isActive ? 'Published to users' : 'Unpublished draft'}
                                />

                                <TextField
                                    label="Title"
                                    value={draft.title}
                                    onChange={(event) => handleDraftChange('title', event.target.value)}
                                    helperText="Optional short label shown before the message."
                                    fullWidth
                                />
                                <TextField
                                    label="Message"
                                    value={draft.message}
                                    onChange={(event) => handleDraftChange('message', event.target.value)}
                                    multiline
                                    minRows={3}
                                    required
                                    fullWidth
                                />
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField
                                        select
                                        label="Severity"
                                        value={draft.severity}
                                        onChange={(event) => handleDraftChange('severity', event.target.value as UserNotificationSeverity)}
                                        fullWidth
                                    >
                                        {severityOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        label="Priority"
                                        type="number"
                                        value={draft.priority}
                                        onChange={(event) => handleDraftChange('priority', Number(event.target.value))}
                                        fullWidth
                                    />
                                </Stack>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField
                                        label="Starts at"
                                        type="datetime-local"
                                        value={draft.startsAt}
                                        onChange={(event) => handleDraftChange('startsAt', event.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Ends at"
                                        type="datetime-local"
                                        value={draft.endsAt}
                                        onChange={(event) => handleDraftChange('endsAt', event.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />
                                </Stack>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField
                                        label="CTA label"
                                        value={draft.ctaLabel}
                                        onChange={(event) => handleDraftChange('ctaLabel', event.target.value)}
                                        fullWidth
                                    />
                                    <TextField
                                        label="CTA URL"
                                        value={draft.ctaUrl}
                                        onChange={(event) => handleDraftChange('ctaUrl', event.target.value)}
                                        placeholder="/mock-tests or https://..."
                                        fullWidth
                                    />
                                </Stack>
                                <TextField
                                    label="Coupon code"
                                    value={draft.couponCode}
                                    onChange={(event) => handleDraftChange('couponCode', event.target.value)}
                                    helperText="Optional. App shows a copy action when set."
                                    fullWidth
                                />

                                <Alert severity={selectedSeverity.color}>
                                    <Stack spacing={0.5}>
                                        {draft.title.trim() ? <Typography fontWeight={800}>{draft.title.trim()}</Typography> : null}
                                        <Typography>{draft.message.trim() || 'Notification message preview'}</Typography>
                                        {draft.couponCode.trim() ? (
                                            <Typography variant="caption">Coupon: {draft.couponCode.trim()}</Typography>
                                        ) : null}
                                    </Stack>
                                </Alert>

                                <Stack spacing={0.5}>
                                    <Typography variant="caption" color="text.secondary">
                                        Starts: {draft.startsAt || 'Immediately'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Ends: {draft.endsAt || 'Until unpublished'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Created: {selectedNotification ? formatDateTime(selectedNotification.createdAt) : 'N/A'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Updated: {selectedNotification ? formatDateTime(selectedNotification.updatedAt) : 'N/A'}
                                    </Typography>
                                </Stack>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                    <Button variant="contained" onClick={handleSave} disabled={isSaving || isDeleting}>
                                        {isCreating ? 'Create Notification' : 'Save Changes'}
                                    </Button>
                                    <Button variant="outlined" onClick={handleCreateNew} disabled={isSaving || isDeleting}>
                                        Reset Draft
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<DeleteOutlineRoundedIcon />}
                                        onClick={handleDelete}
                                        disabled={!selectedNotification || isSaving || isDeleting}
                                    >
                                        Delete
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}
